import { isEmpty, isEqual } from 'lodash';
import {
  Color,
  Combine,
  Euler,
  IUniform,
  LineBasicMaterial,
  LineBasicMaterialParameters,
  MeshBasicMaterial,
  MeshBasicMaterialParameters,
  MultiplyOperation,
  ShaderLib,
  ShaderMaterial,
  ShaderMaterialParameters,
  Texture,
  UniformsUtils,
} from 'three';
import { H3Defines, H3SharedUniforms } from '../shared/definitions';
import { h3MainSharedVertexShader } from './shaders/main';

interface H3BasicMaterialParameters
  extends ShaderMaterialParameters,
    MeshBasicMaterialParameters,
    LineBasicMaterialParameters {}

export interface H3Material<U extends H3SharedUniforms = H3SharedUniforms>
  extends ShaderMaterial {
  defines: H3Defines;

  setUniform<K extends keyof U>(key: K, value: U[K]): void;
  assignDefines<K extends keyof H3Defines>(defines: Pick<H3Defines, K>): void;
}

export class H3BasicMaterial<U extends H3SharedUniforms = H3SharedUniforms>
  extends ShaderMaterial
  implements H3Material<U>, MeshBasicMaterial, LineBasicMaterial
{
  override defines: H3Defines = {
    USE_LUT_PROJECTION: false,
    USE_CYLINDRICAL_PROJECTION: false,
    USE_DEFORMED_CYLINDRICAL_PROJECTION: false,
    USE_FISHEYE_PROJECTION: false,
    USE_MOTION_COMPENSATION: false,
    USE_BROWN_CONRADY_PROJECTION: false,
    IS_POINT: false,
    IS_CUBOID: false,
  };

  // Copy of default values
  // MeshBasicMaterial
  isMeshBasicMaterial: any = false; // hackily implementing interface
  color = new Color(0xffffff);
  map: Texture | null = null;
  lightMap: Texture | null = null;
  lightMapIntensity = 1;
  aoMap: Texture | null = null;
  aoMapIntensity = 1;
  specularMap: Texture | null = null;
  alphaMap: Texture | null = null;
  envMap: Texture | null = null;
  envMapRotation = new Euler();
  combine: Combine = MultiplyOperation;
  reflectivity = 1;
  refractionRatio = 0.98;
  wireframeLinecap = 'round';
  wireframeLinejoin = 'round';
  // LineBasicMaterial
  isLineBasicMaterial: any = false; // hackily implementing interface
  linecap = 'round';
  linejoin = 'round';

  constructor(parameters?: H3BasicMaterialParameters) {
    super();

    this.vertexShader = h3MainSharedVertexShader;
    this.fragmentShader = ShaderLib.basic.fragmentShader;

    this.precision = 'highp';

    this.uniforms = UniformsUtils.merge([
      this.uniforms,
      ShaderLib.basic.uniforms,
    ]);

    if (parameters) {
      this.setValues(parameters);
    }
  }

  setUniform<K extends keyof U>(key: K, value: U[K]) {
    this.uniforms[key as string] = { value };
    this.uniformsNeedUpdate = true;
  }

  assignDefines<K extends keyof H3Defines>(defines: Pick<H3Defines, K>) {
    const newDefines: H3Defines = { ...this.defines, ...defines };

    // Prevents triggering a shader update which requires a program compilation
    if (isEqual(this.defines, newDefines)) {
      return;
    }

    this.defines = { ...this.defines, ...defines };
    this.needsUpdate = true;
  }

  override setValues(parameters: H3BasicMaterialParameters) {
    if (isEmpty(parameters)) {
      return;
    }
    super.setValues(parameters);

    refreshUniformsCommon(this.uniforms, this);
  }
}

// excerpt from
// https://github.com/mrdoob/three.js/blob/r164/src/renderers/webgl/WebGLMaterials.js#L120
function refreshUniformsCommon(
  uniforms: ShaderMaterial['uniforms'],
  material: H3BasicMaterial,
) {
  uniforms.opacity.value = material.opacity;

  uniforms.diffuse.value.copy(material.color);

  if (material.map) {
    uniforms.map.value = material.map;

    refreshTransformUniform(material.map, uniforms.mapTransform);
  }
}

// excerpt from
// https://github.com/mrdoob/three.js/blob/r164/src/renderers/webgl/WebGLMaterials.js#L120
function refreshTransformUniform(map: Texture, uniform: IUniform) {
  if (map.matrixAutoUpdate) {
    map.updateMatrix();
  }

  uniform.value.copy(map.matrix);
}
